import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import {
  DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { mdiDragVertical, mdiPlus, mdiTrashCanOutline } from '@mdi/js'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Checkbox } from '../ui/checkbox'
import { Input } from '../ui/input'
import { AppIcon } from '../ui/icon'
import { Alert, AlertDescription } from '../ui/alert'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog'
import { AssetPicker } from '../AssetPicker'
import { useAssetTypes } from '../../api/queries'
import { useCan } from '../../contexts/PermissionsContext'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { usePropertyDefinitions, usePropertyEnumValues } from '../../hooks/useQueries'
import { getLocalizedName } from '../../lib/localization'
import { PageHeader } from '../shared/PageHeader'
import { SECTION_ICONS } from '../../lib/section-icons'
import { ConditionBuilder, type ValueOption } from './ConditionBuilder'
import { pickLabel } from './colour'
import {
  type ClassSpec, type DefinitionSpec, type Target, type ValueKind, type View,
  ELIGIBLE_PROPERTY_TYPES, MAX_CLASSES, SOURCE_KINDS, TARGETS, VIEWS,
  blankClass, blankDefinition, canBeBare, moveClass, slugify, valueKindOf, viewsFor,
} from './definition'
import { ApiFailure, useDeleteThematicDefinition, usePreviewThematicDefinition, useSaveThematicDefinition, useThematicDefinition } from './definitionApi'

const SELECT_CLASS = 'h-9 rounded-md border border-input bg-background px-2 text-sm'

interface EditorProps {
  org: string
  /** The code of the definition to edit, or `new`. */
  code: string
  /** True right after the creation redirect. */
  justSaved?: boolean
}

/** One property the picker offers. */
interface PropertyOption {
  uid: string
  code: string
  type: string
  label: string
  unit?: string
}

function SortableClass({
  cls, index, kind, options, unit, readOnly, onChange, onRemove,
}: {
  cls: ClassSpec
  index: number
  kind: ValueKind | null
  options: ValueOption[]
  unit?: string
  readOnly: boolean
  onChange: (next: ClassSpec) => void
  onRemove: () => void
}) {
  const { t } = useTranslation('thematics')
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: cls.code, disabled: readOnly })
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 }}
      className="flex gap-2 rounded-md border bg-card p-3"
      data-testid={`definition-class-${index}`}
    >
      <button
        type="button"
        className="mt-1 cursor-grab text-muted-foreground"
        aria-label={t('definitions.classes.drag')}
        data-testid={`definition-class-${index}-handle`}
        {...attributes}
        {...listeners}
      >
        <AppIcon path={mdiDragVertical} size="sm" />
      </button>
      <div className="min-w-0 flex-1 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="color"
            value={cls.color}
            onChange={(e) => onChange({ ...cls, color: e.target.value.toUpperCase() })}
            className="h-9 w-10 cursor-pointer rounded border border-input bg-background p-1"
            aria-label={t('definitions.classes.colour')}
            data-testid={`definition-class-${index}-colour`}
          />
          <Input
            className="w-28 font-mono text-xs"
            value={cls.color}
            onChange={(e) => onChange({ ...cls, color: e.target.value })}
            aria-label={t('definitions.classes.colour_hex')}
            data-testid={`definition-class-${index}-hex`}
          />
          <Input
            className="w-44"
            value={cls.label.en ?? ''}
            placeholder={t('definitions.classes.label_en')}
            onChange={(e) => onChange({ ...cls, label: { ...cls.label, en: e.target.value } })}
            data-testid={`definition-class-${index}-label-en`}
          />
          <Input
            className="w-44"
            value={cls.label.fr ?? ''}
            placeholder={t('definitions.classes.label_fr')}
            onChange={(e) => onChange({ ...cls, label: { ...cls.label, fr: e.target.value } })}
            data-testid={`definition-class-${index}-label-fr`}
          />
          <Input
            className="w-32 font-mono text-xs"
            value={cls.code}
            onChange={(e) => onChange({ ...cls, code: e.target.value })}
            aria-label={t('definitions.classes.code')}
            data-testid={`definition-class-${index}-code`}
          />
        </div>
        <ConditionBuilder
          kind={kind}
          condition={cls.condition}
          onChange={(condition) => onChange({ ...cls, condition })}
          options={options}
          unit={unit}
          testId={`definition-class-${index}-condition`}
        />
      </div>
      {!readOnly && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onRemove}
          aria-label={t('definitions.classes.remove')}
          title={t('definitions.classes.remove')}
          data-testid={`definition-class-${index}-remove`}
        >
          <AppIcon path={mdiTrashCanOutline} size="sm" />
        </Button>
      )}
    </div>
  )
}

/**
 * The editor of one stored thematic (spec 2026-10-06-11): names, what it
 * colours, where it reads its value, the ordered classes with a condition
 * builder that follows the property type, the two implicit classes, the views it
 * is suggested in, and a live preview on an asset the user picks.
 */
export function ThematicDefinitionEditor({ org, code, justSaved = false }: EditorProps) {
  const { t, i18n } = useTranslation('thematics')
  const lang = i18n.language || 'en'
  const navigate = useNavigate()
  const isNew = code === 'new'
  const canWrite = useCan('configuration.write')
  const existing = useThematicDefinition(org, isNew ? undefined : code)
  const save = useSaveThematicDefinition(org)
  const remove = useDeleteThematicDefinition(org)
  const preview = usePreviewThematicDefinition(org)

  const [draft, setDraft] = useState<DefinitionSpec>(blankDefinition)
  const [codeTouched, setCodeTouched] = useState(false)
  const [errors, setErrors] = useState<{ name: string, message: string }[]>([])
  const [warnings, setWarnings] = useState<string[]>([])
  const [saved, setSaved] = useState(justSaved)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [previewAsset, setPreviewAsset] = useState<{ uid: string, name: string } | null>(null)
  const [previewView, setPreviewView] = useState<View | null>(null)
  const [query, setQuery] = useState('')

  useEffect(() => {
    if (existing.data) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { uid, created_at, updated_at, ...spec } = existing.data
      setDraft({ ...blankDefinition(), ...spec })
    }
  }, [existing.data])

  const properties = usePropertyDefinitions(org, { entityType: 'asset', dataTypes: [...ELIGIBLE_PROPERTY_TYPES] })
  const propertyOptions: PropertyOption[] = useMemo(() => {
    const out: PropertyOption[] = []
    for (const p of properties.data) {
      const flags = p.flags ?? []
      const multiple = (p.properties as { multiple?: boolean } | undefined)?.multiple
      if (!p.enabled || flags.includes('pii') || multiple || p.code.endsWith('_provenance')) continue
      const unit = (p.properties as { unit?: string } | undefined)?.unit
      out.push({ uid: p.uid, code: p.code, type: p.data_type, label: getLocalizedName(p.name, lang) || p.code, unit })
    }
    return out.sort((a, b) => a.label.localeCompare(b.label))
  }, [properties.data, lang])
  const selected = propertyOptions.find((p) => p.code === draft.source.property_code)
  const kind = valueKindOf(draft.source.kind, selected?.type)

  const enumValues = usePropertyEnumValues(org, selected?.uid ?? '', { enabled: kind === 'enum' && !!selected })
  const assetTypes = useAssetTypes(org)
  const options: ValueOption[] = useMemo(() => {
    if (kind === 'enum') return enumValues.data.map((v) => ({ value: v.value, label: pickLabel(v.name, lang, v.value) }))
    if (kind === 'asset_type') return (assetTypes.data ?? []).map((a) => ({ value: a.code ?? '', label: (typeof a.name === 'string' ? a.name : getLocalizedName(a.name, lang)) || a.code || '' }))
    return []
  }, [kind, enumValues.data, assetTypes.data, lang])

  const readOnly = !canWrite
  const set = (patch: Partial<DefinitionSpec>) => { setDraft((d) => ({ ...d, ...patch })); setSaved(false) }
  const views = viewsFor(draft.targets)
  const view = previewView && views.includes(previewView) ? previewView : views[0] ?? null

  useBreadcrumbs([
    { title: t('definitions.breadcrumb.home'), path: `/${org}` },
    { title: t('definitions.breadcrumb.types'), path: `/${org}/types` },
    { title: t('definitions.breadcrumb.thematics'), path: `/${org}/types/thematics` },
    { title: isNew ? t('definitions.new') : code, path: `/${org}/types/thematics/${code}` },
  ])

  // The live preview: the draft, evaluated on the picked asset, shortly after
  // the last edit.
  const { mutate: runPreview, reset: resetPreview } = preview
  useEffect(() => {
    if (!previewAsset || !view || !kind) { resetPreview(); return }
    const id = setTimeout(() => {
      runPreview({ assetUid: previewAsset.uid, view, definition: { ...draft, code: draft.code || 'preview' } })
    }, 400)
    return () => clearTimeout(id)
  }, [draft, previewAsset, view, kind, runPreview, resetPreview])

  const dnd = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }))
  const onDragEnd = (e: DragEndEvent) => {
    if (!e.over || e.active.id === e.over.id) return
    const classes = draft.classes ?? []
    set({ classes: moveClass(classes, classes.findIndex((c) => c.code === e.active.id), classes.findIndex((c) => c.code === e.over?.id)) })
  }

  const toggleTarget = (target: Target, on: boolean) =>
    set({ targets: TARGETS.filter((x) => (x === target ? on : draft.targets.includes(x))) })

  const setSourceKind = (sourceKind: DefinitionSpec['source']['kind']) =>
    set({ source: { kind: sourceKind }, classes: [] })

  const onSave = async () => {
    setErrors([])
    setWarnings([])
    try {
      const res = await save.mutateAsync({ code: isNew ? undefined : code, spec: draft })
      setWarnings(res.warnings ?? [])
      setSaved(true)
      if (isNew) await navigate({ to: '/$org/types/thematics/$code', params: { org, code: draft.code }, search: { saved: true }, replace: true })
    } catch (e) {
      const fail = e instanceof ApiFailure ? e : new ApiFailure(String(e))
      setErrors(fail.fields.length ? fail.fields : [{ name: '', message: fail.message }])
    }
  }

  const onDelete = async () => {
    await remove.mutateAsync(code)
    await navigate({ to: '/$org/types/thematics', params: { org } })
  }

  if (!isNew && existing.isLoading) return <div data-testid="definition-loading" className="text-sm text-muted-foreground">{t('definitions.loading')}</div>
  if (!isNew && existing.isError) return <div data-testid="definition-not-found" className="text-sm text-destructive">{t('definitions.not_found')}</div>

  const classes = draft.classes ?? []
  const bare = classes.length === 0
  const filteredProperties = propertyOptions.filter((p) => !query || p.label.toLowerCase().includes(query.toLowerCase()) || p.code.includes(query.toLowerCase()))

  return (
    <div className="space-y-6" data-testid="definition-editor">
      <PageHeader
        icon={SECTION_ICONS.types}
        title={isNew ? t('definitions.new') : (pickLabel(draft.name, lang, code) || code)}
        description={t('definitions.editor_description')}
        actions={!readOnly && (
          <div className="flex items-center gap-2">
            {!isNew && (
              <Button type="button" variant="destructive" size="icon" onClick={() => setConfirmDelete(true)} aria-label={t('definitions.delete')} title={t('definitions.delete')} data-testid="definition-delete">
                <AppIcon path={mdiTrashCanOutline} size="sm" />
              </Button>
            )}
            <Button type="button" onClick={onSave} disabled={save.isPending} data-testid="definition-save">{t('definitions.save')}</Button>
          </div>
        )}
      />

      {readOnly && <Alert data-testid="definition-read-only"><AlertDescription>{t('definitions.read_only')}</AlertDescription></Alert>}
      {errors.length > 0 && (
        <Alert variant="destructive" data-testid="definition-errors">
          <AlertDescription>
            <ul className="list-disc pl-4">
              {errors.map((er, i) => <li key={i}>{er.name ? `${er.name}: ` : ''}{er.message}</li>)}
            </ul>
          </AlertDescription>
        </Alert>
      )}
      {saved && warnings.length > 0 && (
        <Alert data-testid="definition-warnings">
          <AlertDescription>
            <ul className="list-disc pl-4">{warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
          </AlertDescription>
        </Alert>
      )}
      {saved && warnings.length === 0 && <div className="text-sm text-muted-foreground" data-testid="definition-saved">{t('definitions.saved')}</div>}

      <fieldset disabled={readOnly} className="space-y-6">
        <Card>
          <CardHeader><CardTitle>{t('definitions.general')}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-3">
              <Input
                className="w-64"
                value={draft.name.en ?? ''}
                placeholder={t('definitions.name_en')}
                data-testid="definition-name-en"
                onChange={(e) => {
                  const name = { ...draft.name, en: e.target.value }
                  set({ name, ...(isNew && !codeTouched ? { code: slugify(e.target.value) } : {}) })
                }}
              />
              <Input
                className="w-64"
                value={draft.name.fr ?? ''}
                placeholder={t('definitions.name_fr')}
                data-testid="definition-name-fr"
                onChange={(e) => set({ name: { ...draft.name, fr: e.target.value } })}
              />
              <Input
                className="w-48 font-mono text-xs"
                value={draft.code}
                disabled={!isNew}
                placeholder={t('definitions.code')}
                aria-label={t('definitions.code')}
                data-testid="definition-code"
                onChange={(e) => { setCodeTouched(true); set({ code: e.target.value }) }}
              />
            </div>

            <div>
              <div className="mb-1 text-sm font-medium">{t('definitions.targets')}</div>
              <div className="flex flex-wrap gap-4">
                {TARGETS.map((target) => (
                  <label key={target} className="flex items-center gap-1 text-sm">
                    <Checkbox checked={draft.targets.includes(target)} onCheckedChange={(on) => toggleTarget(target, on)} data-testid={`definition-target-${target}`} />
                    {t(`definitions.target.${target}`)}
                  </label>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <div className="text-sm font-medium">{t('definitions.source')}</div>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  className={SELECT_CLASS}
                  value={draft.source.kind}
                  onChange={(e) => setSourceKind(e.target.value as DefinitionSpec['source']['kind'])}
                  data-testid="definition-source-kind"
                  aria-label={t('definitions.source')}
                >
                  {SOURCE_KINDS.map((s) => <option key={s} value={s}>{t(`definitions.source_kind.${s}`)}</option>)}
                </select>
                {draft.source.kind === 'property' && (
                  <>
                    <Input className="w-48" value={query} placeholder={t('definitions.search_property')} onChange={(e) => setQuery(e.target.value)} data-testid="definition-property-search" />
                    <select
                      className={`${SELECT_CLASS} w-72`}
                      value={draft.source.property_code ?? ''}
                      onChange={(e) => set({ source: { kind: 'property', property_code: e.target.value || undefined }, classes: [] })}
                      data-testid="definition-property"
                      aria-label={t('definitions.property')}
                    >
                      <option value="">{t('definitions.pick_property')}</option>
                      {selected && !filteredProperties.includes(selected) && <option value={selected.code}>{selected.label}</option>}
                      {filteredProperties.map((p) => (
                        <option key={p.code} value={p.code} data-testid={`definition-property-option-${p.code}`}>
                          {p.label} ({p.type}{p.unit ? `, ${p.unit}` : ''})
                        </option>
                      ))}
                    </select>
                  </>
                )}
              </div>
              {draft.source.kind === 'property' && (draft.targets.includes('room') || draft.targets.includes('equipment')) && (
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={!!draft.via_grouper} onCheckedChange={(on) => set({ via_grouper: on })} data-testid="definition-via-grouper" />
                  {t('definitions.via_grouper')}
                </label>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>{t('definitions.classes.title')}</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {bare && kind && canBeBare(kind) && <p className="text-sm text-muted-foreground" data-testid="definition-bare">{t('definitions.classes.bare')}</p>}
            {bare && kind && !canBeBare(kind) && <p className="text-sm text-muted-foreground" data-testid="definition-needs-classes">{t('definitions.classes.needs_classes')}</p>}
            {!kind && <p className="text-sm text-muted-foreground" data-testid="definition-pick-source">{t('definitions.classes.pick_source')}</p>}
            <DndContext sensors={dnd} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={classes.map((c) => c.code)} strategy={verticalListSortingStrategy}>
                {classes.map((cls, i) => (
                  <SortableClass
                    key={cls.code + i}
                    cls={cls}
                    index={i}
                    kind={kind}
                    options={options}
                    unit={selected?.unit}
                    readOnly={readOnly}
                    onChange={(next) => set({ classes: classes.map((c, j) => (j === i ? next : c)) })}
                    onRemove={() => set({ classes: classes.filter((_, j) => j !== i) })}
                  />
                ))}
              </SortableContext>
            </DndContext>
            <div className="flex flex-wrap items-center gap-4">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!kind || classes.length >= MAX_CLASSES}
                onClick={() => set({ classes: [...classes, blankClass(classes, kind)] })}
                data-testid="definition-add-class"
              >
                <AppIcon path={mdiPlus} size="sm" className="mr-1" />{t('definitions.classes.add')}
              </Button>
              <span className="text-xs text-muted-foreground">{classes.length}/{MAX_CLASSES}</span>
            </div>
            <div className="flex flex-wrap gap-6 pt-2">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={!!draft.other_class} onCheckedChange={(on) => set({ other_class: on })} data-testid="definition-other-class" />
                {t('definitions.other_class')}
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={!!draft.unset_class} onCheckedChange={(on) => set({ unset_class: on })} data-testid="definition-unset-class" />
                {t('definitions.unset_class')}
              </label>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>{t('definitions.suggested_in')}</CardTitle></CardHeader>
          <CardContent>
            <p className="mb-2 text-xs text-muted-foreground">{t('definitions.suggested_help')}</p>
            <div className="flex flex-wrap gap-4">
              {VIEWS.map((v) => (
                <label key={v} className="flex items-center gap-2 text-sm">
                  {t(`definitions.view.${v}`)}
                  <Input
                    className="w-20"
                    inputMode="numeric"
                    placeholder={t('definitions.rank')}
                    defaultValue={draft.suggested_in?.[v] ?? ''}
                    data-testid={`definition-suggested-${v}`}
                    onChange={(e) => {
                      const next = { ...(draft.suggested_in ?? {}) }
                      const n = Number(e.target.value)
                      if (e.target.value.trim() === '' || !Number.isInteger(n) || n < 1) delete next[v]
                      else next[v] = n
                      set({ suggested_in: next })
                    }}
                  />
                </label>
              ))}
            </div>
          </CardContent>
        </Card>
      </fieldset>

      <Card data-testid="definition-preview">
        <CardHeader><CardTitle>{t('definitions.preview.title')}</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">{t('definitions.preview.help')}</p>
          <div className="flex flex-wrap items-start gap-3">
            <div className="w-80">
              <AssetPicker
                org={org}
                value={previewAsset?.uid}
                valueLabel={previewAsset?.name}
                onChange={(uid, name) => setPreviewAsset({ uid, name })}
                testId="definition-preview-asset"
                placeholder={t('definitions.preview.pick')}
              />
            </div>
            {views.length > 1 && (
              <select className={SELECT_CLASS} value={view ?? ''} onChange={(e) => setPreviewView(e.target.value as View)} data-testid="definition-preview-view" aria-label={t('definitions.preview.view')}>
                {views.map((v) => <option key={v} value={v}>{t(`definitions.view.${v}`)}</option>)}
              </select>
            )}
          </div>
          {preview.isError && <div className="text-sm text-destructive" data-testid="definition-preview-error">{preview.error instanceof ApiFailure ? (preview.error.fields[0]?.message ?? preview.error.message) : t('definitions.preview.error')}</div>}
          {preview.data && <PreviewTable result={preview.data} lang={lang} />}
        </CardContent>
      </Card>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent data-testid="definition-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('definitions.delete_title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('definitions.delete_description')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('definitions.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete} data-testid="definition-delete-confirm">{t('definitions.delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

function PreviewTable({ result, lang }: { result: NonNullable<ReturnType<typeof usePreviewThematicDefinition>['data']>, lang: string }) {
  const { t } = useTranslation('thematics')
  const total = result.coverage?.total ?? 0
  const other = (result.classes ?? []).find((c) => c.value === '__other')
  const share = total > 0 && other ? Math.round(((other.count ?? 0) / total) * 100) : 0
  return (
    <div className="space-y-2" data-testid="definition-preview-result">
      <div className="text-xs text-muted-foreground" data-testid="definition-preview-coverage">
        {t('definitions.preview.coverage', { covered: result.coverage?.covered ?? 0, total })}
      </div>
      <ul className="space-y-1">
        {(result.classes ?? []).map((c) => (
          <li key={c.value} className="flex items-center gap-2 text-sm" data-testid={`definition-preview-row-${c.value}`}>
            <span className="inline-block h-3 w-3 rounded-sm" style={{ background: c.colour }} />
            <span>{pickLabel(c.label, lang, c.value)}</span>
            <span className="font-semibold" data-testid={`definition-preview-row-${c.value}-count`}>{c.count ?? 0}</span>
          </li>
        ))}
        {(result.unset?.count ?? 0) > 0 && (
          <li className="text-sm text-muted-foreground" data-testid="definition-preview-unset">{t('unset')}: {result.unset?.count}</li>
        )}
        {(result.unmatched?.count ?? 0) > 0 && (
          <li className="text-sm text-muted-foreground" data-testid="definition-preview-unmatched">{t('unmatched')}: {result.unmatched?.count}</li>
        )}
        {(result.not_applicable?.count ?? 0) > 0 && (
          <li className="text-sm text-muted-foreground" data-testid="definition-preview-not-applicable">{t('not_applicable')}: {result.not_applicable?.count}</li>
        )}
      </ul>
      {other && <div className="text-sm" data-testid="definition-preview-other-share">{t('definitions.preview.other_share', { share })}</div>}
      {(result.warnings ?? []).length > 0 && (
        <ul className="list-disc pl-4 text-xs text-muted-foreground" data-testid="definition-preview-warnings">
          {(result.warnings ?? []).map((w, i) => <li key={i}>{w}</li>)}
        </ul>
      )}
    </div>
  )
}
